import { DEFAULT_RULES, extractRules } from './types.js';
export function tickDamageReactRules(state) {
    pruneExpiredDamageReacts(state);
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        const rules = extractRules(entity).damageReacts;
        for (const rule of rules) {
            if (rule.trigger.stat === 'event')
                continue;
            if (matchesStatTrigger(entity, state, rule)) {
                activateDamageReactRule(state, entity.id, rule);
            }
        }
    }
    applyEffectiveThreatPolicies(state);
}
export function activateDamageReactEvent(state, entityId, event) {
    // The current catalogue uses allyHp% for "critical ally" behavior. Keep the
    // unused event literal honest rather than guessing a crit-hit interpretation.
    if (event === 'ally_critical')
        return;
    const entity = state.entities.find((e) => e.id === entityId);
    if (entity === undefined || entity.hp <= 0)
        return;
    for (const rule of extractRules(entity).damageReacts) {
        const trigger = rule.trigger;
        if (trigger.stat === 'event' && trigger.event === event) {
            activateDamageReactRule(state, entity.id, rule);
        }
    }
}
export function resolveActiveDamageReactOverrides(active, tick) {
    const best = new Map();
    const activeRuleIds = new Set();
    for (const item of active ?? []) {
        if (item.expiresAt <= tick)
            continue;
        activeRuleIds.add(item.sourceRuleId);
        const prev = best.get(item.category);
        if (prev === undefined ||
            item.priority > prev.priority ||
            (item.priority === prev.priority && item.firedAt >= prev.firedAt)) {
            best.set(item.category, item);
        }
    }
    const out = { activeRuleIds: [...activeRuleIds] };
    const movement = best.get('movement');
    if (movement !== undefined)
        out.movement = movement.ruleId;
    const tempo = best.get('tempo');
    if (tempo !== undefined)
        out.tempo = tempo.ruleId;
    const positionPolicy = best.get('positionPolicy');
    if (positionPolicy !== undefined) {
        out.positionPolicy = positionPolicy.ruleId;
    }
    const threatPolicy = best.get('threatPolicy');
    if (threatPolicy !== undefined)
        out.threatPolicy = threatPolicy.ruleId;
    return out;
}
export function threatModForPolicy(rule) {
    switch (rule) {
        case 'threat_low':
            return 0.5;
        case 'threat_neutral':
            return 1;
        case 'threat_high':
            return 1.5;
        case 'threat_overdrive':
            return 2;
        case 'threat_defender':
            // MVP approximation of docs/04 "ally defended" conditional +30%.
            return 1.3;
    }
}
function activateDamageReactRule(state, entityId, rule) {
    let list = state.damageReactOverrides.get(entityId);
    if (list === undefined) {
        list = [];
        state.damageReactOverrides.set(entityId, list);
    }
    for (const override of rule.overrides) {
        const existing = list.find((item) => item.sourceRuleId === rule.ruleId && item.category === override.category);
        const next = {
            sourceRuleId: rule.ruleId,
            category: override.category,
            ruleId: override.ruleId,
            priority: rule.priority,
            firedAt: state.tick,
            expiresAt: state.tick + rule.durationTicks,
        };
        if (existing !== undefined) {
            existing.ruleId = next.ruleId;
            existing.priority = next.priority;
            existing.firedAt = next.firedAt;
            existing.expiresAt = next.expiresAt;
        }
        else {
            list.push(next);
        }
    }
}
function pruneExpiredDamageReacts(state) {
    for (const [entityId, list] of state.damageReactOverrides) {
        const next = list.filter((item) => item.expiresAt > state.tick);
        if (next.length === 0)
            state.damageReactOverrides.delete(entityId);
        else
            state.damageReactOverrides.set(entityId, next);
    }
}
function applyEffectiveThreatPolicies(state) {
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        const rules = extractRules(entity);
        const active = resolveActiveDamageReactOverrides(state.damageReactOverrides.get(entity.id), state.tick);
        const threatPolicy = active.threatPolicy ?? rules.threatPolicy ?? DEFAULT_RULES.threatPolicy;
        entity.threatMod = threatModForPolicy(threatPolicy);
    }
}
function matchesStatTrigger(entity, state, rule) {
    const { trigger } = rule;
    switch (trigger.stat) {
        case 'hp%':
            return comparePercent((entity.hp / Math.max(1, entity.hpMax)) * 100, trigger);
        case 'shield%': {
            const shield = entity.shields.reduce((sum, layer) => sum + Math.max(0, layer.amount), 0);
            return comparePercent((shield / Math.max(1, entity.hpMax)) * 100, trigger);
        }
        case 'allyHp%': {
            let lowest = Infinity;
            for (const ally of state.entities) {
                if (ally.id === entity.id || ally.side !== entity.side || ally.hp <= 0)
                    continue;
                lowest = Math.min(lowest, (ally.hp / Math.max(1, ally.hpMax)) * 100);
            }
            return Number.isFinite(lowest) && comparePercent(lowest, trigger);
        }
        case 'event':
            return false;
    }
}
function comparePercent(value, trigger) {
    if (trigger.threshold === undefined)
        return false;
    return (trigger.op ?? 'lt') === 'gt' ? value > trigger.threshold : value < trigger.threshold;
}
//# sourceMappingURL=damage-react.js.map